import { useQuestionContext } from "./QuestionProvider";
import {
  QuestionContextValue,
  QuestionType,
  SingleSelectReturn,
  MultiSelectReturn,
  OpenEndedReturn,
  AnatomyReturn,
} from "./types";
import { useLocalQuestionState } from "./useLocalQuestionState";

/* ---------------------------- State Factory ---------------------------- */

type StateGetter = (
  context: QuestionContextValue,
  componentId: string | number
) => Record<string, unknown>;

const stateGetterMap: Record<QuestionType, StateGetter> = {
  [QuestionType.SingleSelect]: (context, componentId) => {
    const state = context.getSingleSelectState(componentId);
    return {
      selectedOptionIndex: state.selectedOptionIndex,
      submittedOptionIndexes: state.submittedOptionIndexes,
    };
  },
  [QuestionType.MultiSelect]: (context, componentId) => {
    const state = context.getMultiSelectState(componentId);
    return {
      selectedOptionIds: state.selectedOptionIds,
      submittedOptionIds: state.submittedOptionIds,
      attemptsCount: state.attemptsCount,
      hasAttempted: state.hasAttempted,
    };
  },
  [QuestionType.OpenEnded]: (context, componentId) => {
    const state = context.getOpenEndedState(componentId);
    return {
      value: state.value,
      isCompleted: state.isCompleted,
      textChanged: state.textChanged,
    };
  },
  [QuestionType.Anatomy]: (context, componentId) => {
    const state = context.getAnatomyState(componentId);
    return {
      answers: state.answers,
      correctAnswers: state.correctAnswers,
      incorrectAnswers: state.incorrectAnswers,
      isSubmitted: state.isSubmitted,
      showFeedback: state.showFeedback,
    };
  },
};

function createQuestionState(
  context: QuestionContextValue,
  type: QuestionType,
  componentId: string | number
) {
  return stateGetterMap[type](context, componentId);
}

type QuestionReturnType<T extends QuestionType> =
  T extends QuestionType.SingleSelect
    ? SingleSelectReturn
    : T extends QuestionType.MultiSelect
      ? MultiSelectReturn
      : T extends QuestionType.Anatomy
        ? AnatomyReturn
        : OpenEndedReturn;

export function useQuestion<T extends QuestionType>(
  options: { componentId?: string | number; type: T },
  contextOverride?: QuestionContextValue
): QuestionReturnType<T> {
  const { componentId, type } = options;

  const contextFromProvider = useQuestionContext();
  const context = contextOverride ?? contextFromProvider;

  // Local state - always called to satisfy React rules
  const localState = useLocalQuestionState();

  // Use local state if no provider context
  if (context === null || componentId === undefined) {
    return localState as unknown as QuestionReturnType<T>;
  }

  // Use provider state
  const state = createQuestionState(context, type, componentId);
  const handlers = context.getQuestionHandlers(componentId, type);
  return { ...state, ...handlers } as unknown as QuestionReturnType<T>;
}
